import React, { useRef, useEffect, useState } from 'react';

interface CampaignInsightsPreviewChartProps {
    targets: number;
    deliveries: number;
    messagesRead: number;
    uniqueClicks: number;
    conversions: number;
}

const CampaignInsightsPreviewChart: React.FC<CampaignInsightsPreviewChartProps> = ({
    targets,
    deliveries,
    messagesRead,
    uniqueClicks,
    conversions,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

    const data = [
        { label: 'Targets', value: targets },
        { label: 'Deliveries', value: deliveries },
        { label: 'Messages Read', value: messagesRead },
        { label: 'Unique Clicks', value: uniqueClicks },
        { label: 'Conversions', value: conversions },
    ];

    useEffect(() => {
        const observer = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            setSize({ width, height });
        });
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    const { width, height } = size;
    const padding = 20;
    const maxValue = Math.max(...data.map(d => d.value), 1);

    const points: [number, number][] = data.map((d, i): [number, number] => {
        const x = padding + (i * (width - 2 * padding)) / (data.length - 1);
        const y = height - padding - ((d.value / maxValue) * (height - 2 * padding));
        return [x, y];
    });

    const getSmoothPath = (points: [number, number][]) => {
        if (points.length < 2) return '';
        let d = `M${points[0][0]},${points[0][1]}`;
        for (let i = 1; i < points.length; i++) {
            const [x0, y0] = points[i - 1];
            const [x1, y1] = points[i];
            const cx = (x0 + x1) / 2;
            d += ` Q${x0},${y0} ${cx},${(y0 + y1) / 2}`;
        }
        d += ` L${points[points.length - 1][0]},${points[points.length - 1][1]}`;
        return d;
    };

    const pathD = getSmoothPath(points);
    const fillPathD = `${pathD} L${points[points.length - 1][0]},${height - padding} L${points[0][0]},${height - padding} Z`;

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
            {width > 0 && height > 0 && (
                <svg width={width} height={height}>
                    <defs>
                        <linearGradient id="myGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#fff" />
                            <stop offset="8%" stopColor="#f3f5ff" />
                        </linearGradient>
                    </defs>
                    <defs>
                        <linearGradient id="myGradientLine" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#fff" />
                            <stop offset="8%" stopColor="#f0f1f9" />
                        </linearGradient>
                    </defs>
                    <path d={fillPathD} fill="url(#myGradient)" stroke="none" />
                    <path d={pathD} fill="none" stroke="url(#myGradientLine)" strokeWidth={1} />
                </svg>
            )}
        </div>
    );
};

export default CampaignInsightsPreviewChart;